import { useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import {
  FetchCreatorsInSavedList,
  useCreatorsListFromSavedList,
  useSavedListOutreachStatuses,
  useUpdateSavedListOutreachStatus,
  type CreatorsInListDTO,
  type SavedListOutreachChannelDTO,
  type SavedListOutreachStatusDTO,
  type SavedListOutreachStatusItemDTO,
} from '@/entities/saved-creators-list';

import { useExportCreatorListWithFeedback } from '@/entities/creator';
import { useRemoveProfilesFromList } from '@/entities/saved-creators-list/mutation/saved-creator-list';
import { useSavedList } from '@/entities/saved-creators-list/query/use-saved-list';
import { Button } from '@/shared/components/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  createSavedListCampaignCreatorDraftKey,
  saveSavedListCampaignCreatorDraft,
} from '../campaign/ui/campaign-create/helpers/saved-list-campaign-creator-draft-storage';
import { enrichSavedListCreatorsWithMockEmails } from './helpers/saved-list-mock-email';
import {
  SAVED_LIST_OUTREACH_STAGE_LABELS,
  SAVED_LIST_OUTREACH_STAGE_OPTIONS,
  getConfirmedSavedListCreatorIds,
  getSavedListCreatorOutreachStage,
  type SavedListOutreachStage,
} from './helpers/saved-list-outreach-status';
import { SavedProfileDetailViewHeader } from './saved-profiles.header';
import { SavedListOutreachPanel } from './ui/outreach/saved-list-outreach-panel';
import { SavedListCreatorsTable } from './ui/table/saved-list-creators-table';

export function SavedCreatorsListDetailPage() {
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [activeStage, setActiveStage] = useState<SavedListOutreachStage>('not_contacted');
  const { listId } = useParams({ strict: false });
  if (!listId) throw new Error('[SavedCreatorsListDetailPage] listId is required');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();
  const { workspaceId } = useWorkspaceId();
  const { data: listInfo } = useSavedList(listId);
  const {
    data: creatorsList,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCreatorsListFromSavedList(listId);
  const list = useMemo(() => {
    return enrichSavedListCreatorsWithMockEmails(creatorsList.pages.map((p) => p.creators).flat());
  }, [creatorsList.pages]);
  const outreachStatusesQuery = useSavedListOutreachStatuses(listId);
  const updateOutreachStatusMutation = useUpdateSavedListOutreachStatus();
  const outreachStatuses = useMemo(
    () => outreachStatusesQuery.data ?? [],
    [outreachStatusesQuery.data],
  );
  const confirmedCreatorIds = useMemo(
    () =>
      getConfirmedSavedListCreatorIds(
        outreachStatuses,
        Array.from(new Set(outreachStatuses.map((status) => status.creator_id))),
      ),
    [outreachStatuses],
  );
  const creatorsByStage = useMemo(() => {
    return SAVED_LIST_OUTREACH_STAGE_OPTIONS.reduce(
      (groups, stage) => {
        groups[stage] = list.filter(
          (creator) => getSavedListCreatorOutreachStage(outreachStatuses, creator.id) === stage,
        );
        return groups;
      },
      {
        not_contacted: [],
        sent: [],
        confirmed: [],
        declined: [],
      } as Record<SavedListOutreachStage, CreatorsInListDTO[]>,
    );
  }, [list, outreachStatuses]);
  const visibleCreators = creatorsByStage[activeStage];
  const visibleCreatorIdSet = useMemo(
    () => new Set(visibleCreators.map((creator) => creator.id)),
    [visibleCreators],
  );
  const visibleSelectedProfiles = useMemo(
    () => selectedProfiles.filter((creatorId) => visibleCreatorIdSet.has(creatorId)),
    [selectedProfiles, visibleCreatorIdSet],
  );
  const removeProfilesMutation = useRemoveProfilesFromList();
  const exportCreatorListWithFeedback = useExportCreatorListWithFeedback();

  const handleRemove = useCallback(
    async (profile_id: string) => {
      await removeProfilesMutation.mutateAsync({
        listId: listId,
        request: {
          creator_ids: [profile_id],
        },
      });
      await refetch();
      setSelectedProfiles((prev) => prev.filter((id) => id !== profile_id));
      toast('Creator removed from list');
    },
    [listId, refetch, removeProfilesMutation],
  );

  const handleExportCsv = useCallback(
    async (ids?: string[]) => {
      await exportCreatorListWithFeedback(
        listId,
        listInfo.title,
        ids ? { creator_ids: ids, selected_all: false } : { selected_all: true },
      );
    },
    [exportCreatorListWithFeedback, listId, listInfo.title],
  );

  const handleBack = useCallback(() => {
    navigate({ to: '..' });
  }, [navigate]);

  const handleStageChange = useCallback((stage: string) => {
    setActiveStage(stage as SavedListOutreachStage);
    setSelectedProfiles([]);
  }, []);

  const handleCreateCampaign = useCallback(async () => {
    if (confirmedCreatorIds.length === 0) return;

    const confirmedIdSet = new Set(confirmedCreatorIds);
    const confirmedCreators = [];
    let page = 1;
    let totalPages = 1;

    try {
      do {
        const response = await FetchCreatorsInSavedList(listId, { page, limit: 100 });
        confirmedCreators.push(
          ...response.creators
            .filter((creator) => confirmedIdSet.has(creator.id))
            .map((creator) => ({
              id: creator.id,
              username: creator.username,
              platform: creator.platform,
            })),
        );
        totalPages = response.total_pages;
        page += 1;
      } while (page <= totalPages && confirmedCreators.length < confirmedCreatorIds.length);
    } catch {
      toast.error('Failed to load confirmed creator details.');
      return;
    }

    if (confirmedCreators.length === 0) {
      toast.error('Confirmed creator details are still loading.');
      return;
    }

    const sourceDraftKey = createSavedListCampaignCreatorDraftKey();
    const draftSaved = saveSavedListCampaignCreatorDraft(sourceDraftKey, {
      sourceListId: listId,
      creators: confirmedCreators,
    });

    if (!draftSaved) {
      toast.error('Failed to prepare confirmed creators for campaign creation.');
      return;
    }

    navigate({
      to: '/workspace/$workspaceId/campaigns/create',
      params: { workspaceId },
      search: {
        source: 'confirmed-outreach',
        sourceListId: listId,
        sourceDraftKey,
      },
    });
  }, [confirmedCreatorIds, listId, navigate, workspaceId]);

  const handleOutreachStatusChange = useCallback(
    async ({
      channel,
      creatorId,
      status,
    }: {
      channel: SavedListOutreachChannelDTO;
      creatorId: string;
      status: SavedListOutreachStatusDTO;
    }) => {
      try {
        await updateOutreachStatusMutation.mutateAsync({
          listId,
          request: {
            creator_id: creatorId,
            channel,
            status,
          },
        });
      } catch {
        toast.error('Failed to update outreach status');
      }
    },
    [listId, updateOutreachStatusMutation],
  );

  const handleQueueCreated = useCallback(
    ({
      channel,
      creatorIds,
    }: {
      channel: SavedListOutreachChannelDTO;
      creatorIds: string[];
    }) => {
      const queryKey = queryKeys.savedCreatorsList.outreachStatuses(listId);

      queryClient.setQueryData<SavedListOutreachStatusItemDTO[]>(queryKey, (current = []) => {
        const nextStatuses = [...current];

        for (const creatorId of creatorIds) {
          const existingIndex = nextStatuses.findIndex(
            (item) => item.creator_id === creatorId && item.channel === channel,
          );
          const nextStatus: SavedListOutreachStatusItemDTO = {
            creator_id: creatorId,
            channel,
            status: 'queued',
            updated_at: new Date().toISOString(),
          };

          if (existingIndex === -1) {
            nextStatuses.push(nextStatus);
            continue;
          }

          nextStatuses[existingIndex] = {
            ...nextStatuses[existingIndex],
            ...nextStatus,
          };
        }

        return nextStatuses;
      });

      setActiveStage('sent');
      setSelectedProfiles([]);
      void outreachStatusesQuery.refetch();
    },
    [listId, outreachStatusesQuery, queryClient, queryKeys.savedCreatorsList],
  );

  return (
    <div className="flex h-screen flex-col">
      <SavedProfileDetailViewHeader
        listInfo={listInfo}
        selectedCreators={visibleSelectedProfiles}
        handleBackToList={handleBack}
        handleExportSelected={() => handleExportCsv(visibleSelectedProfiles)}
        handleExportList={() => handleExportCsv()}
      />

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto p-6">
          <div className="mb-5 flex flex-col gap-4">
            <Tabs value={activeStage} onValueChange={handleStageChange}>
              <TabsList className="grid w-full grid-cols-4">
                {SAVED_LIST_OUTREACH_STAGE_OPTIONS.map((stage) => (
                  <TabsTrigger key={stage} value={stage} className="gap-2">
                    <span>{SAVED_LIST_OUTREACH_STAGE_LABELS[stage]}</span>
                    <span className="bg-muted-foreground/10 text-muted-foreground rounded-full px-2 py-0.5 text-xs font-semibold">
                      {creatorsByStage[stage].length}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {activeStage === 'confirmed' ? (
              <div className="border-border bg-muted/30 flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
                <div>
                  <p className="text-foreground text-sm font-semibold">
                    {confirmedCreatorIds.length} confirmed creators are ready for campaign setup.
                  </p>
                  <p className="text-muted-foreground text-sm">
                    Create a campaign draft with everyone in the Confirmed stage.
                  </p>
                </div>
                <Button
                  className="shrink-0"
                  disabled={confirmedCreatorIds.length === 0}
                  onClick={handleCreateCampaign}
                >
                  <Send className="size-4" />
                  Create campaign
                </Button>
              </div>
            ) : null}
          </div>

          <SavedListCreatorsTable
            creators={visibleCreators}
            disabled={updateOutreachStatusMutation.isPending}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            outreachStatuses={outreachStatuses}
            selectedCreatorIds={visibleSelectedProfiles}
            onRemoveCreator={(creatorId) => {
              void handleRemove(creatorId);
            }}
            onSelectedCreatorIdsChange={setSelectedProfiles}
            onStatusChange={handleOutreachStatusChange}
          />
        </div>
        {activeStage === 'not_contacted' ? (
          <SavedListOutreachPanel
            listId={listId}
            creators={visibleCreators}
            selectedCreatorIds={visibleSelectedProfiles}
            confirmedCreatorCount={confirmedCreatorIds.length}
            onCreateCampaign={handleCreateCampaign}
            onQueueCreated={handleQueueCreated}
            showCampaignAction={false}
          />
        ) : null}
      </div>
    </div>
  );
}
